<template>
  <div>
    <PropertyEditor />
    <LoadingSpinner v-if="loading" />
    <WorkPacks
      :title="`Parts on Equipment ${transferFrom.equipmentTag}`"
      :headers="headers"
      :data="data"
      :toolbar-buttons="toolbarButtons(['batchDelete', 'transfer', 'closeout'], shouldDisableButtons)"
      @onToolbarButtonClicked="handleToolbarButtonClicked"
      @onSelectedRowsChanged="handleSelectedRowsChanged"
      @onActionRequested="handleActionRequested"
    />
    <CloseOutDialog
      :initial-show="showSelectTargetWorkPack"
      :options="availableWorkPacks"
      :option-ids="workPacks"
      :transfer-from="transferFrom"
      :transfer-items="selectedRows.map(({ assetClass }) => assetClass)"
      @onTransfer="handleTransferParts"
      @onCloseout="handleCloseoutParts"
      @onCancel="handleCancelTransfer"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { WorkPackLevel, WorkPackItemAction, queryStringBuilder } from '@/utils';
import { LoadingSpinner } from '@/react';
import { PropertyEditor, CloseOutDialog } from './components';
import WorkPacks from './WorkPacks.vue';

export default {
  name: 'WorkPackPartsContainer',
  components: {
    LoadingSpinner,
    PropertyEditor,
    WorkPacks,
    CloseOutDialog,
  },
  props: {
    selectedRows: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showSelectTargetWorkPack: false,
    };
  },
  computed: {
    ...mapGetters({
      loading: 'workPacks/loading',
      openWorkPacks: 'workPacks/openWorkPackNames',
      openWorkPackIds: 'workPacks/openWorkPackIds',
      headers: 'workPacks/currentLevelHeaders',
      data: 'workPacks/data',
      toolbarButtons: 'workPacks/currentLevelToolbarButtonsWithDisabledState',
      currentLevelDisplayName: 'workPacks/currentLevelDisplayName',
      pathToCurrentLevel: 'workPacks/pathToCurrentLevel',
    }),
    workPacks() {
      return Object.fromEntries(
        this.openWorkPacks
          .map((name, idx) => [name, this.openWorkPackIds[idx]])
          .filter(([name]) => name !== this.pathToCurrentLevel[0].name)
      );
    },
    availableWorkPacks() {
      return Object.keys(this.workPacks);
    },
    transferFrom() {
      const [{ name: workPack } = {}, { name: equipmentTag } = {}] = this.pathToCurrentLevel;
      return {
        workPack,
        equipmentTag,
      };
    },
    shouldDisableButtons() {
      return (
        this.selectedRows.length === 0 ||
        this.selectedRows.some(({ state }) => ['transferred', 'remediated'].includes(state))
      );
    },
  },
  created() {
    this.initialise();
  },
  methods: {
    ...mapActions({
      initialise: 'workPacks/initialise',
      deleteWorkPackPart: 'workPacks/delete',
      transferWorkPackParts: 'workPacks/transfer',
      closeoutWorkPackParts: 'workPacks/closeout',
      setNotification: 'workPacks/setNotification',
      setError: 'workPacks/setError',
    }),
    handleToolbarButtonClicked(item) {
      switch (item) {
        case 'batchDelete':
          this.handleBatchDelete();
          break;
        case 'transfer':
          this.handleBatchTransfer();
          break;
        case 'closeout':
          this.handleCloseoutParts();
          break;
        default:
          break;
      }
    },
    handleSelectedRowsChanged(rows) {
      this.$emit('onSelectedRowsChanged', rows);
    },
    handleActionRequested({ column, row, event }) {
      switch (column) {
        case WorkPackItemAction.DELETE:
          this.handleDelete(row);
          break;
        default:
          this.handleNextStage(row, event);
          break;
      }
    },
    handleNextStage(row, event) {
      if (event.ctrlKey || event.metaKey) {
        const queryString = queryStringBuilder({
          id: this.$route.query.id,
          wpType: this.$route.query.wpType,
        });
        window.open(`${this.$route.path}/${row.id}?${queryString}`);
      } else {
        this.$router.push({
          name: WorkPackLevel.VIEWS,
          query: {
            id: this.$route.query.id,
            wpType: this.$route.query.wpType,
          },
          params: { wpPartId: row.id },
        });
      }
    },
    handleDelete(row) {
      this.$emit('onDeleteRequested', `You are about to delete ${this.currentLevelDisplayName} ${row.assetId}`, () =>
        this.deleteWorkPackPart(row.id)
      );
    },
    handleBatchDelete() {
      this.$emit(
        'onBatchDelete',
        `You are about to delete ${this.selectedRows.length}
          ${this.currentLevelDisplayName}${this.selectedRows.length > 1 ? 's' : ''}`,
        ({ id }) => this.deleteWorkPackPart(id)
      );
    },
    handleBatchTransfer() {
      this.showSelectTargetWorkPack = true;
    },
    handleCloseoutParts() {
      this.$emit(
        'onBatchCloseout',
        `You are about to closeout ${this.selectedRows.length}
          ${this.currentLevelDisplayName}${this.selectedRows.length > 1 ? 's' : ''}`,
        () => this.closeoutWorkPackParts(this.selectedRows.map(({ id }) => id))
      );
      this.showSelectTargetWorkPack = false;
    },
    async handleTransferParts(destWpName, destWpId) {
      try {
        await this.transferWorkPackParts({ destWpId, items: this.selectedRows.map((row) => row.id) });
        this.setNotification(
          `${this.currentLevelDisplayName}${this.selectedRows.length > 1 ? 's' : ''} transferred succesfully`
        );
      } catch (error) {
        this.setError(error.message);
      }
      this.showSelectTargetWorkPack = false;
    },
    handleCancelTransfer() {
      this.showSelectTargetWorkPack = false;
    },
  },
};
</script>
